import { Fraction } from '../numeric/fraction';
import { simplifySqrt } from '../numeric/radical';

export type Pt = { x: number; y: number };

/**
 * Computes the midpoint of two lattice points.
 * Returns exact coordinates as Fractions.
 */
export function midpoint(a: Pt, b: Pt): [Fraction, Fraction] {
  const mx = new Fraction(a.x + b.x, 2);
  const my = new Fraction(a.y + b.y, 2);
  return [mx, my];
}

/**
 * Computes the slope between two lattice points.
 * Returns 'vertical' if x-coordinates are equal, otherwise a Fraction (rise/run).
 */
export function slope(a: Pt, b: Pt): Fraction | 'vertical' {
  if (a.x === b.x) return 'vertical';
  const rise = b.y - a.y;
  const run = b.x - a.x;
  return new Fraction(rise, run);
}

/**
 * Computes the section point dividing AB in ratio m:n from A.
 * Uses the section formula: P = (n*A + m*B) / (m + n)
 * All arithmetic in Fraction for exactness.
 */
export function sectionPoint(a: Pt, b: Pt, m: number, n: number): [Fraction, Fraction] {
  const denom = new Fraction(m + n, 1);
  const nFrac = new Fraction(n, 1);
  const mFrac = new Fraction(m, 1);

  const aXFrac = new Fraction(a.x, 1);
  const aYFrac = new Fraction(a.y, 1);
  const bXFrac = new Fraction(b.x, 1);
  const bYFrac = new Fraction(b.y, 1);

  const px = nFrac.mul(aXFrac).add(mFrac.mul(bXFrac)).div(denom);
  const py = nFrac.mul(aYFrac).add(mFrac.mul(bYFrac)).div(denom);

  return [px, py];
}

/**
 * Computes the signed area of a polygon using the Shoelace formula.
 * Positive if vertices are counterclockwise (y-up convention), negative if clockwise.
 * Returns an exact Fraction.
 */
export function shoelaceAreaExact(points: readonly Pt[]): Fraction {
  if (points.length < 3) return Fraction.ZERO;

  let sum = new Fraction(0, 1);

  for (let i = 0; i < points.length; i += 1) {
    const curr = points[i]!;
    const next = points[(i + 1) % points.length]!;

    const currXFrac = new Fraction(curr.x, 1);
    const currYFrac = new Fraction(curr.y, 1);
    const nextXFrac = new Fraction(next.x, 1);
    const nextYFrac = new Fraction(next.y, 1);

    // Shoelace: x_i * y_{i+1} - x_{i+1} * y_i
    const term = currXFrac.mul(nextYFrac).sub(nextXFrac.mul(currYFrac));
    sum = sum.add(term);
  }

  return sum.mul(new Fraction(1, 2));
}

/**
 * Computes the squared Euclidean distance between two lattice points.
 * Returns { n: squared distance, simplified: [a, b] } where n = a²·b, b square-free.
 */
export function latticeDistance(a: Pt, b: Pt): { n: number; simplified: [number, number] } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const n = dx * dx + dy * dy;
  const simplified = simplifySqrt(n);
  return { n, simplified };
}

/**
 * Computes the perpendicular bisector of segment AB.
 * Returns { point: midpoint, dir: direction vector perpendicular to AB }.
 */
export function perpBisector(
  a: Pt,
  b: Pt,
): { point: { x: number; y: number }; dir: { x: number; y: number } } {
  const [mx, my] = midpoint(a, b);
  const point = { x: mx.toNumber(), y: my.toNumber() };

  // Direction perpendicular to AB: if AB = (dx, dy), then perp = (-dy, dx)
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dir = { x: -dy, y: dx };

  return { point, dir };
}

/**
 * Checks if two slopes are perpendicular.
 * Vertical + Horizontal → true
 * Vertical + Vertical → false
 * For Fractions: two slopes m₁, m₂ are perpendicular if m₁ * m₂ = -1
 */
export function arePerpendicular(s1: Fraction | 'vertical', s2: Fraction | 'vertical'): boolean {
  // Case: one is vertical
  if (s1 === 'vertical' && s2 === 'vertical') {
    return false;
  }
  if (s1 === 'vertical') {
    // s2 must be a Fraction; vertical is perpendicular to slope 0 only
    return (s2 as Fraction).num === 0;
  }
  if (s2 === 'vertical') {
    // s1 must be a Fraction; vertical is perpendicular to slope 0 only
    return s1.num === 0;
  }

  // Both are Fractions; check if m₁ * m₂ = -1
  const product = s1.mul(s2);
  return product.equals(new Fraction(-1, 1));
}
